import React, { useEffect, useState, useCallback } from 'react';
import { ArrowClockwise, ArrowSquareOut, CaretDown, CaretUp, CheckCircle, LinkSimple, PencilSimple, Sparkle, User, Users, Warning, X } from '@phosphor-icons/react';
import { BestaetigenDialog, SchwebenderToast, useToastPill } from '../../../components/immobilien/ui';
import { supabase } from '../../../lib/supabase/client';
import { ladeAnalysenFuerEmails, analysiereEmail, ladeKiModell, ladeVorschlagUndRegel, ladeSchwelle, setzeCockpitStatus, type EmailAnalyse } from '../../../lib/ki/analyseApi';
import { analyseZuTicketVorlage, type TicketVorlage } from '../../../lib/ki/ticketVorlage';
import { erstelleTicketAusAnalyse, type VorgangStart } from '../../../lib/ki/erstelleTicketAusAnalyse';
import VorgangStartKarte from './VorgangStartKarte';
import { ladeTicketsZuEmail, verknuepfeTicketMitEmail, loeseTicketEmailVerknuepfung } from '../../../lib/ki/ticketLink';
import { findeTicketFuerReply, haengeEmailAnTicket } from '../../../lib/ki/replyErkennung';

interface Props {
  emailId: string;
  kiStatus: string | null;
  ticketKategorien: { id: string; name: string; farbe: string | null }[];
  istSuperAdmin?: boolean;
  email?: { betreff?: string | null; plainText?: string | null; objekt_id?: string | null; mieter_id?: string | null; absender?: string | null; datum?: string | null; in_reply_to?: string | null };
  onCreateTicket?: (vorlage: TicketVorlage) => void;
  ticketRefresh?: number;
  userId?: string | null;
  onChanged?: () => void;
  /** Mobil: 44-px-Ziele. */
  mobil?: boolean;
}

const PRIO_TON: Record<string, { bg: string; fg: string }> = {
  niedrig: { bg: 'var(--fill2)', fg: 'var(--ink2)' }, mittel: { bg: 'var(--info-soft)', fg: 'var(--info)' },
  hoch: { bg: 'var(--warn-soft)', fg: 'var(--warn)' }, dringend: { bg: 'var(--bad-soft)', fg: 'var(--bad)' },
};

const PILLE: React.CSSProperties = { height: 22, padding: '0 9px', borderRadius: 11, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' };

export default function KiAnalysePanel({ emailId, kiStatus, ticketKategorien, istSuperAdmin = false, email, onCreateTicket, ticketRefresh = 0, userId = null, onChanged, mobil = false }: Props) {
  const [offen, setOffen] = useState(true);
  const [toast, zeigeToast] = useToastPill();
  const [loesFrage, setLoesFrage] = useState<{ id: string; nummer: string } | null>(null);
  const [analyse, setAnalyse] = useState<EmailAnalyse | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [modell, setModell] = useState<'mistral' | 'gemini'>('mistral');
  const [vorschlag, setVorschlag] = useState<{ name: string; telefon: string | null; email: string | null } | null>(null);
  const [regel, setRegel] = useState<{ name: string } | null>(null);
  const [tickets, setTickets] = useState<{ id: string; ticket_nummer: string }[]>([]);
  const [bearbeiterName, setBearbeiterName] = useState<string | null>(null);
  const [replyTicket, setReplyTicket] = useState<{ ticketId: string; ticketNummer: string } | null>(null);
  const [kategorienVoll, setKategorienVoll] = useState<{ id: string; parent_id: string | null }[]>([]);
  const [schwelle, setSchwelle] = useState(0.8);
  const [aktionLaedt, setAktionLaedt] = useState(false);
  const [aktionFehler, setAktionFehler] = useState<string | null>(null);
  const [zuordnenOffen, setZuordnenOffen] = useState(false);
  const [alleTickets, setAlleTickets] = useState<{ id: string; ticket_nummer: string; titel: string | null; objekt_id: string | null }[]>([]);
  const [ticketSuche, setTicketSuche] = useState('');
  const [zuordnenLaedt, setZuordnenLaedt] = useState(false);

  // Einmalig laden: volle Kategorien-Liste mit parent_id (die Prop hat nur id/name/farbe)
  // sowie die Auto-Zuordnungs-Schwelle — beides für analyseZuTicketVorlage/erstelleTicketAusAnalyse.
  useEffect(() => {
    supabase.from('ticket_kategorien').select('id, parent_id').then(({ data }) => { if (data) setKategorienVoll(data); });
    ladeSchwelle().then(setSchwelle);
  }, []);

  const laden = useCallback(async () => {
    const map = await ladeAnalysenFuerEmails([emailId]);
    const a = map[emailId] || null;
    setAnalyse(a);
    if (a && !a.fehler) {
      const vr = await ladeVorschlagUndRegel(a);
      setVorschlag(vr.vorschlag);
      setRegel(vr.regel);

      if (a.bearbeiter_vorschlag_id && a.bearbeiter_vorschlag_typ === 'team') {
        const { data } = await supabase.from('teams').select('name').eq('id', a.bearbeiter_vorschlag_id).maybeSingle();
        setBearbeiterName(data?.name ?? null);
      } else if (a.bearbeiter_vorschlag_id && a.bearbeiter_vorschlag_typ === 'profile') {
        const { data } = await supabase.from('profiles').select('full_name').eq('id', a.bearbeiter_vorschlag_id).maybeSingle();
        setBearbeiterName(data?.full_name ?? null);
      } else {
        setBearbeiterName(null);
      }
    } else {
      setVorschlag(null);
      setRegel(null);
      setBearbeiterName(null);
    }

    const ticketsData = await ladeTicketsZuEmail(emailId);
    setTickets(ticketsData);
    if (ticketsData.length === 0) {
      const rt = await findeTicketFuerReply({ betreff: email?.betreff ?? null, in_reply_to: email?.in_reply_to ?? null });
      setReplyTicket(rt);
    } else {
      setReplyTicket(null);
    }
  }, [emailId, email?.betreff, email?.in_reply_to]);

  useEffect(() => { laden(); ladeKiModell().then(setModell); }, [laden, ticketRefresh]);

  const oeffneZuordnen = async () => {
    setZuordnenOffen(o => !o);
    if (alleTickets.length === 0) {
      const { data } = await supabase.from('tickets')
        .select('id, ticket_nummer, titel, objekt_id, status')
        .not('status', 'in', '(erledigt,geschlossen)');
      setAlleTickets((data as any[]) || []);
    }
  };

  const loeseTicket = async (ticketId: string) => {
    setLoesFrage(null);
    try {
      await loeseTicketEmailVerknuepfung(ticketId, emailId);
      await laden();
      onChanged?.();
    } catch (err: any) {
      zeigeToast('Aufheben fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    }
  };

  const zuordnenTicket = async (ticketId: string) => {
    setZuordnenLaedt(true);
    try {
      await verknuepfeTicketMitEmail(ticketId, emailId);
      setZuordnenOffen(false);
      setTicketSuche('');
      await laden();
      onChanged?.();
    } catch (err: any) {
      zeigeToast('Zuordnen fehlgeschlagen: ' + (err?.message || 'Unbekannt'), true);
    } finally {
      setZuordnenLaedt(false);
    }
  };

  const erneutAnalysieren = async () => {
    setLaedt(true);
    try { await analysiereEmail(emailId, modell, true); await laden(); }
    catch (e) { console.error(e); }
    finally { setLaedt(false); }
  };

  const freigeben = async () => {
    if (!analyse) return;
    setAktionLaedt(true); setAktionFehler(null);
    try {
      await erstelleTicketAusAnalyse(analyse, email || {}, vorschlag, { kategorien: kategorienVoll, schwelle }, userId);
      await laden();
      onChanged?.();
    } catch (e: any) {
      setAktionFehler(e?.message || 'Freigabe fehlgeschlagen');
    } finally {
      setAktionLaedt(false);
    }
  };

  // #489: Vorgang starten = Ticket aus der Analyse plus Vorgangsart, Checkliste, Fristen, Rückfragen
  const starteVorgang = async (vorgang: VorgangStart) => {
    if (!analyse) return;
    setAktionLaedt(true); setAktionFehler(null);
    try {
      const t = await erstelleTicketAusAnalyse(analyse, email || {}, vorschlag, { kategorien: kategorienVoll, schwelle }, userId, vorgang);
      zeigeToast(`Vorgang gestartet – Ticket ${t.ticket_nummer}`);
      await laden();
      onChanged?.();
    } catch (e: any) {
      setAktionFehler(e?.message || 'Vorgang starten fehlgeschlagen');
    } finally {
      setAktionLaedt(false);
    }
  };

  const anhaengen = async () => {
    if (!replyTicket) return;
    setAktionLaedt(true); setAktionFehler(null);
    try {
      await haengeEmailAnTicket(replyTicket.ticketId, emailId, email?.plainText || '', userId);
      await laden();
      onChanged?.();
    } catch (e: any) {
      setAktionFehler(e?.message || 'Anhängen fehlgeschlagen');
    } finally {
      setAktionLaedt(false);
    }
  };

  const ignorieren = async () => {
    if (!analyse) return;
    setAktionLaedt(true); setAktionFehler(null);
    try {
      await setzeCockpitStatus(analyse.id, 'verworfen');
      await laden();
      onChanged?.();
    } catch (e: any) {
      setAktionFehler(e?.message || 'Verwerfen fehlgeschlagen');
    } finally {
      setAktionLaedt(false);
    }
  };

  const rueckgaengig = async () => {
    if (!analyse) return;
    setAktionLaedt(true); setAktionFehler(null);
    try {
      await setzeCockpitStatus(analyse.id, 'offen');
      await laden();
      onChanged?.();
    } catch (e: any) {
      setAktionFehler(e?.message || 'Zurücksetzen fehlgeschlagen');
    } finally {
      setAktionLaedt(false);
    }
  };

  const kat = analyse?.kategorie_id ? ticketKategorien.find((k) => k.id === analyse.kategorie_id) : null;
  const inArbeit = kiStatus === 'offen' || kiStatus === 'laeuft' || laedt;
  const hatTicket = tickets.length > 0;
  const istVerworfen = analyse?.cockpit_status === 'verworfen';

  const kh = mobil ? 44 : 30;
  const knopf = (hervor = false): React.CSSProperties => ({
    height: mobil ? 44 : 34, padding: '0 14px', borderRadius: mobil ? 22 : 17, border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
    fontSize: 12, fontWeight: hervor ? 800 : 700, cursor: 'pointer', whiteSpace: 'nowrap',
    background: hervor ? 'var(--acc-fill)' : 'var(--bg)', color: hervor ? 'var(--acc-ink)' : 'var(--ink)', boxShadow: hervor ? 'var(--acc-sh)' : 'none',
    opacity: aktionLaedt ? 0.5 : 1,
  });

  return (
    <section className="flex flex-col" style={{ borderRadius: 22, padding: mobil ? 12 : 16, gap: 10, background: 'linear-gradient(160deg, var(--acc-soft), var(--fill))', boxShadow: 'inset 0 0 0 1px var(--line)' }}>
      <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
        <span className="flex items-center" style={{ gap: 6, fontSize: 13, fontWeight: 800, color: 'var(--acc)' }}>
          <Sparkle size={16} weight="fill" /> KI-Analyse
        </span>
        <span className="flex-1" />
        {istSuperAdmin && (
          <span className="relative flex" style={{ width: 110 }}>
            <select value={modell} onChange={(e) => setModell(e.target.value as 'mistral' | 'gemini')} aria-label="KI-Modell" className="w-full cursor-pointer"
              style={{ appearance: 'none', WebkitAppearance: 'none', height: kh, padding: '0 28px 0 10px', borderRadius: 10, border: 'none', outline: 'none', background: 'var(--bg)', color: 'var(--ink)', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' }}>
              <option value="mistral">Mistral</option>
              <option value="gemini">Gemini</option>
            </select>
            <CaretDown size={11} weight="bold" color="var(--ink3)" className="absolute pointer-events-none" style={{ right: 10, top: '50%', transform: 'translateY(-50%)' }} />
          </span>
        )}
        <button type="button" onClick={erneutAnalysieren} disabled={laedt} className="flex items-center cursor-pointer"
          style={{ height: kh, padding: '0 10px', gap: 5, borderRadius: 10, border: 'none', background: 'var(--bg)', color: 'var(--ink)', fontSize: 12, fontWeight: 700, opacity: laedt ? 0.5 : 1 }}>
          <ArrowClockwise size={12} weight="bold" className={laedt ? 'animate-spin' : ''} /> Erneut analysieren
        </button>
        <button type="button" onClick={() => setOffen(o => !o)} aria-expanded={offen} title="KI-Analyse ein-/ausklappen" aria-label="KI-Analyse ein-/ausklappen"
          className="immo-hover-flaeche flex items-center justify-center cursor-pointer"
          style={{ width: kh, height: kh, borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink3)' }}>
          {offen ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
        </button>
      </div>

      {offen && (
        <>
          {inArbeit && !analyse && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Analyse läuft…</span>}
          {analyse?.fehler && (
            <span className="flex items-center" style={{ gap: 5, fontSize: 13, color: 'var(--bad)' }}><Warning size={13} weight="bold" /> Fehler: {analyse.fehler}</span>
          )}

          {analyse && !analyse.fehler && (
            <div className="flex flex-col" style={{ gap: 8, fontSize: 13 }}>
              <div className="flex flex-wrap" style={{ gap: 5 }}>
                {analyse.absender_typ && <span style={{ ...PILLE, background: 'var(--bg)', color: 'var(--ink2)', textTransform: 'capitalize' }}>{analyse.absender_typ}</span>}
                {kat && <span style={{ ...PILLE, background: kat.farbe || 'var(--ink3)', color: '#fff' }}>{kat.name}</span>}
                {analyse.prioritaet && <span style={{ ...PILLE, background: (PRIO_TON[analyse.prioritaet] ?? PRIO_TON.niedrig).bg, color: (PRIO_TON[analyse.prioritaet] ?? PRIO_TON.niedrig).fg }}>{analyse.prioritaet}</span>}
                {analyse.ist_notfall && <span style={{ ...PILLE, background: 'var(--bad)', color: '#fff' }}><span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} /> Notfall</span>}
                {analyse.ist_rechtlich_sensibel && <span style={{ ...PILLE, background: 'var(--warn-soft)', color: 'var(--warn)' }}>⚠ Rechtlich sensibel</span>}
                {typeof analyse.confidence === 'number' && <span style={{ ...PILLE, background: 'var(--bg)', color: 'var(--ink2)' }}>{Math.round(analyse.confidence * 100)}%</span>}
                {regel && <span style={{ ...PILLE, background: 'var(--info-soft)', color: 'var(--info)' }}>Regel: {regel.name}</span>}
              </div>
              {analyse.zusammenfassung && <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>{analyse.zusammenfassung}</p>}
              {analyse.erkannte_aufgabe && <p style={{ margin: 0, color: 'var(--ink2)' }}><b>Aufgabe:</b> {analyse.erkannte_aufgabe}</p>}
              {analyse.naechster_schritt && <p style={{ margin: 0, color: 'var(--ink2)' }}><b>Nächster Schritt:</b> {analyse.naechster_schritt}</p>}
              {bearbeiterName && (
                <p className="flex items-center" style={{ margin: 0, gap: 6, color: 'var(--ink2)' }}>
                  {analyse.bearbeiter_vorschlag_typ === 'team' ? <Users size={14} color="var(--ink3)" className="shrink-0" /> : <User size={14} color="var(--ink3)" className="shrink-0" />}
                  <b>Bearbeiter-Vorschlag:</b> {bearbeiterName}
                </p>
              )}
              {vorschlag && (
                <p style={{ margin: 0, color: 'var(--ink2)' }}><b style={{ color: 'var(--acc)' }}>Dienstleister-Vorschlag:</b> {vorschlag.name}{vorschlag.telefon ? ` · ${vorschlag.telefon}` : ''}{vorschlag.email ? ` · ${vorschlag.email}` : ''}</p>
              )}

              {!hatTicket && !istVerworfen && (
                <div className="flex flex-col" style={{ gap: 8, paddingTop: 4 }}>
                  {!analyse.ist_werbung && (
                    <VorgangStartKarte erkannt={analyse.vorgangsart_id ?? null} ausMail={analyse.vorgang_angaben ?? null}
                      bekannt={{ mieter: !!(analyse.mieter_id || email?.mieter_id), einheit: !!analyse.einheit_id }}
                      laeuft={aktionLaedt} onStart={v => { void starteVorgang(v); }} mobil={mobil} />
                  )}
                  {replyTicket && (
                    <div className="flex items-center flex-wrap" style={{ gap: 8, padding: '10px 12px', borderRadius: 14, background: 'var(--info-soft)' }}>
                      <span className="flex-1" style={{ fontSize: 13, fontWeight: 700, color: 'var(--info)' }}>Antwort auf Ticket #{replyTicket.ticketNummer}?</span>
                      <button type="button" onClick={anhaengen} disabled={aktionLaedt} style={knopf(true)}>
                        <LinkSimple size={13} weight="bold" /> An Ticket anhängen
                      </button>
                    </div>
                  )}
                  <div className="flex flex-wrap" style={{ gap: 6 }}>
                    <button type="button" onClick={freigeben} disabled={aktionLaedt} style={knopf(!replyTicket)}>
                      <CheckCircle size={14} weight="bold" /> Freigeben
                    </button>
                    {onCreateTicket && (
                      <button type="button"
                        onClick={() => onCreateTicket(analyseZuTicketVorlage(analyse, email || {}, vorschlag, { kategorien: kategorienVoll, schwelle }))}
                        disabled={aktionLaedt} style={knopf()}>
                        <PencilSimple size={14} weight="bold" /> Anpassen
                      </button>
                    )}
                    <button type="button" onClick={ignorieren} disabled={aktionLaedt} style={knopf()}>
                      <X size={14} weight="bold" /> Ignorieren
                    </button>
                  </div>
                  {aktionFehler && <p style={{ margin: 0, fontSize: 12, color: 'var(--bad)' }}>{aktionFehler}</p>}
                </div>
              )}

              {istVerworfen && !hatTicket && (
                <div className="flex items-center flex-wrap" style={{ gap: 8, fontSize: 13, color: 'var(--ink3)', paddingTop: 4 }}>
                  <span>Vorschlag verworfen</span>
                  <button type="button" onClick={rueckgaengig} disabled={aktionLaedt} className="cursor-pointer"
                    style={{ minHeight: mobil ? 44 : 24, padding: 0, border: 'none', background: 'transparent', color: 'var(--acc)', fontSize: 13, fontWeight: 800, opacity: aktionLaedt ? 0.5 : 1 }}>Rückgängig</button>
                  {aktionFehler && <span style={{ color: 'var(--bad)' }}>{aktionFehler}</span>}
                </div>
              )}

              {hatTicket && (
                <div className="flex flex-wrap" style={{ gap: 6, paddingTop: 4 }}>
                  {tickets.map((t) => (
                    <span key={t.id} className="inline-flex items-center" style={{ height: mobil ? 44 : 30, padding: '0 4px 0 12px', gap: 4, borderRadius: 15, background: 'var(--ok-soft)', color: 'var(--ok)', fontSize: 12, fontWeight: 800 }}>
                      <a href={`/admin/tickets/${t.id}`} className="flex items-center hover:underline" style={{ gap: 5, color: 'inherit' }}>
                        <ArrowSquareOut size={13} weight="bold" /> Ticket {t.ticket_nummer}
                      </a>
                      <button type="button" onClick={() => setLoesFrage({ id: t.id, nummer: t.ticket_nummer })} title="Verknüpfung aufheben" aria-label="Verknüpfung aufheben"
                        className="flex items-center justify-center cursor-pointer"
                        style={{ width: mobil ? 36 : 24, height: mobil ? 36 : 24, borderRadius: '50%', border: 'none', background: 'transparent', color: 'inherit' }}>
                        <X size={12} weight="bold" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
          {!hatTicket && (
            <div className="relative">
              <button type="button" onClick={oeffneZuordnen} aria-expanded={zuordnenOffen} style={knopf()}>
                <LinkSimple size={13} weight="bold" /> Vorhandenem Ticket zuordnen
              </button>
              {zuordnenOffen && (
                <div className="absolute flex flex-col" style={{ left: 0, top: '100%', marginTop: 6, width: 'min(300px, 80vw)', zIndex: 30, borderRadius: 16, padding: 6, background: 'var(--bg)', boxShadow: '0 24px 50px -16px var(--shade), inset 0 0 0 1px var(--line)' }}>
                  <input
                    autoFocus={!mobil}
                    value={ticketSuche}
                    onChange={(e) => setTicketSuche(e.target.value)}
                    placeholder="Ticket suchen (Nr./Titel)…"
                    style={{ height: 40, margin: '0 0 4px', padding: '0 12px', borderRadius: 12, border: 'none', outline: 'none', background: 'var(--fill2)', color: 'var(--ink)', fontSize: mobil ? 16 : 13 }}
                  />
                  <div className="immo-scroll flex flex-col overflow-y-auto" style={{ maxHeight: 224 }}>
                    {alleTickets
                      .filter(t => {
                        const q = ticketSuche.trim().toLowerCase();
                        if (!q) return true;
                        return `${t.ticket_nummer} ${t.titel || ''}`.toLowerCase().includes(q);
                      })
                      .sort((a, b) => {
                        const ao = email?.objekt_id && a.objekt_id === email.objekt_id ? 0 : 1;
                        const bo = email?.objekt_id && b.objekt_id === email.objekt_id ? 0 : 1;
                        return ao - bo;
                      })
                      .slice(0, 30)
                      .map(t => (
                        <button key={t.id} type="button" disabled={zuordnenLaedt} onClick={() => zuordnenTicket(t.id)}
                          className="immo-hover-flaeche w-full text-left cursor-pointer"
                          style={{ minHeight: mobil ? 44 : 32, padding: '4px 10px', borderRadius: 10, border: 'none', background: 'transparent', color: 'var(--ink)', fontSize: 12, opacity: zuordnenLaedt ? 0.5 : 1 }}>
                          <b style={{ color: 'var(--acc)' }}>{t.ticket_nummer}</b> {t.titel || ''}
                        </button>
                      ))}
                    {alleTickets.length === 0 && (
                      <span style={{ padding: '6px 10px', fontSize: 12, color: 'var(--ink3)', fontStyle: 'italic' }}>Keine offenen Tickets</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          {!analyse && !inArbeit && <span style={{ fontSize: 13, color: 'var(--ink3)' }}>Noch keine Analyse.</span>}
        </>
      )}

      {loesFrage && (
        <BestaetigenDialog
          titel="Verknüpfung aufheben"
          text={`Verknüpfung zu Ticket #${loesFrage.nummer} aufheben?`}
          bestaetigenLabel="Aufheben"
          icon={LinkSimple}
          onAbbrechen={() => setLoesFrage(null)}
          onBestaetigen={() => { void loeseTicket(loesFrage.id); }}
        />
      )}
      <SchwebenderToast toast={toast} mobil={mobil} />
    </section>
  );
}
